/**
 * Member Gateway - Create Member
 *
 * This is the ONLY standalone service function (not a React hook).
 * It's called from AuthContext during registration — before the user
 * is authenticated and before ApiProvider is in the React tree.
 *
 * The Cognito user pool doesn't allow direct sign-ups - users must be
 * created through the Member Gateway service which handles Cognito user
 * creation via Admin APIs.
 */

import { MembersApi } from '@bvengineering/cas-member-gateway-service-client';
import { ENV } from '@/config/env';

// A standalone MembersApi instance for pre-auth operations (createMember).
// This does NOT use auth headers — it's called before the user is authenticated.
const membersClient = new MembersApi(undefined, ENV.memberGatewayBasePath);

export interface CreateMemberError {
  error: string;
  message: string;
  statusCode: number;
}

export interface AxiosError<T> {
  data: T;
  status: number;
  statusText: string;
}

/**
 * Create a new member via the Member Gateway service.
 *
 * This is the ONLY service function that remains as a standalone export
 * because it's called from AuthContext during registration — before the
 * user is authenticated and before ApiProvider is in the React tree.
 *
 * @param email - The user's email address
 * @param password - The user's password
 * @returns Promise that resolves when member is created
 * @throws Error with message if creation fails (e.g., weak password, email in use)
 */
export async function createMember(email: string, password: string): Promise<void> {
  try {
    await membersClient.membersControllerCreateMember({
      email,
      password,
    });
  } catch (error: unknown) {
    // Handle 400 errors - these are validation errors (weak password, email in use)
    const axiosError = error as { response?: AxiosError<CreateMemberError> };
    if (axiosError.response?.status === 400) {
      const errorData = axiosError.response.data;
      throw new Error(errorData.message || 'Registration failed');
    }

    // Re-throw other errors
    throw error;
  }
}
